import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { SxProps } from '@mui/material'
import { Box } from '@/components/Layout'
import { GRADIENT_POINTS } from './BannerAnimated.data'
import { bannerAnimatedStyles as styles } from './BannerAnimated.style'
import {
  drawBackground,
  drawAtmosphere,
  drawVignette,
  resolvePointPosition,
  drawGradientBlob,
  buildPhaseOffsets,
  buildEntryTimings,
} from './BannerAnimated.utils'

// ── Component ─────────────────────────────────────────────────────────────────

function BannerAnimated() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [animationStarted, setAnimationStarted] = useState(false)
  // Adjusted so grain appears the same physical size regardless of screen DPR.
  // Target frequency is 1.3 (tuned for 2× DPR); dividing by the actual DPR
  // produces a finer grain on 1× screens where each CSS pixel is larger.
  // Scale frequency up with DPR so grain stays visually fine on high-DPR (mobile) screens.
  // 0.65 is the baseline for 1× screens; a 3× phone gets ~1.95 (finer grain, less visible).
  const [noiseFreq, setNoiseFreq] = useState(0.65)

  /**
   * Sets up the canvas animation lifecycle: computes DPR-adjusted noise
   * frequency, starts the render loop, wires the resize listener, and
   * returns a cleanup function that cancels everything on unmount.
   */
  const initializeBanner = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    setNoiseFreq(0.65 * (window.devicePixelRatio || 1))

    // Pre-compute per-point randomized values once on mount
    const phaseOffsets = buildPhaseOffsets()
    const entryTimings = buildEntryTimings()

    let W = 0
    let H = 0
    let animFrame = 0
    let startTime = 0

    /** Syncs canvas pixel dimensions with its CSS size at the current DPR. */
    function resize(cvs: HTMLCanvasElement) {
      W = cvs.width = cvs.offsetWidth * window.devicePixelRatio
      H = cvs.height = cvs.offsetHeight * window.devicePixelRatio
    }

    /** Main render loop — called every frame via requestAnimationFrame. */
    function animate(renderCtx: CanvasRenderingContext2D, now: number) {
      const elapsed = now - startTime

      drawBackground({ ctx: renderCtx, canvasWidth: W, canvasHeight: H })

      // Draw blobs back-to-front so earlier points aren't fully obscured
      for (let i = GRADIENT_POINTS.length - 1; i >= 0; i--) {
        const { cx, cy } = resolvePointPosition({
          pointIndex: i,
          elapsed,
          entryTimings,
          phaseOffsets,
        })
        drawGradientBlob({
          ctx: renderCtx,
          point: GRADIENT_POINTS[i],
          centerX: cx,
          centerY: cy,
          canvasWidth: W,
          canvasHeight: H,
        })
      }

      drawAtmosphere({ ctx: renderCtx, canvasWidth: W, canvasHeight: H })
      drawVignette({ ctx: renderCtx, canvasWidth: W, canvasHeight: H })

      animFrame = requestAnimationFrame((t) => animate(renderCtx, t))
    }

    /**
     * Kicks off the animation loop and triggers the CSS entry transition
     * (opacity 0→1, scale 1.3→1) by forcing a reflow between state changes.
     */
    function start(cvs: HTMLCanvasElement, renderCtx: CanvasRenderingContext2D) {
      cancelAnimationFrame(animFrame)
      startTime = performance.now()
      animFrame = requestAnimationFrame((t) => animate(renderCtx, t))

      cvs.style.opacity = '0'
      cvs.style.transform = 'scale(1.3)'
      void cvs.offsetWidth // force reflow so the transition triggers
      cvs.style.opacity = '1'
      cvs.style.transform = 'scale(1)'
      setAnimationStarted(true)
    }

    const onResize = () => resize(canvas)
    window.addEventListener('resize', onResize)
    resize(canvas)

    // Brief delay lets the page settle before the animation begins
    const timer = setTimeout(() => start(canvas, ctx), 0)

    return () => {
      cancelAnimationFrame(animFrame)
      clearTimeout(timer)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    return initializeBanner(canvas)
  }, [initializeBanner])

  return (
    <Box sx={styles.wrapper} aria-hidden="true">
      <Box component={'canvas'} ref={canvasRef} sx={styles.canvas} />
      {/* <Box sx={[styles.grainImage, animationStarted && styles.grainImageVisible] as SxProps} /> */}

      {/* Noise grain overlay — always mounted, fades in once the animation starts */}
      <Box component="svg" sx={styles.noiseSvg}>
        <filter id="banner-gradient-noise">
          <feTurbulence
            type="fractalNoise"
            baseFrequency={noiseFreq}
            numOctaves={6}
            stitchTiles="stitch"
          />
          <feGaussianBlur stdDeviation="0.5" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </Box>
      <Box sx={[styles.noiseOverlay, animationStarted && styles.noiseOverlayVisible] as SxProps}>
        <Box sx={styles.noiseFilter} />
      </Box>
    </Box>
  )
}

export default memo(BannerAnimated)
